<template>
  <div :class="classes" :qa-locator-id="locators.set()">
    <div class="icon-wrapper" :class="iconWrapperClasses">
      <Icon
        v-show="!!modelValue"
        class="icon"
        icon="check"
        size="xs"
        :qa-locator-id="locators.set('icon')"
      />
    </div>

    <input
      class="input"
      :value="modelValue"
      :qa-locator-id="locators.set('input')"
      @click.stop="toggleValue"
      @mouseenter="toggleHover(true)"
      @mouseleave="toggleHover(false)"
    />

    <label class="label" :qa-locator-id="locators.set('label')" @click="clickLabel">
      {{ label }}
      <slot name="label" />
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { useQaLocators } from '~/composables/use-qa-locators';

import Icon from '~/components/components/icon/Icon.vue';

const COMPONENT_CLASS_NAME: string = 'checkbox';

const locators = useQaLocators(COMPONENT_CLASS_NAME);

const modelValue = defineModel();

// --- emits ---
const emit = defineEmits(['update:modelValue', 'on-click-label']);

// --- props ---
const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  isToggleLabel: {
    type: Boolean,
    default: true,
  },
});

const isIconWrapperHover = ref(false);

const classes = computed(() => {
  return {
    [COMPONENT_CLASS_NAME]: true,
    'toggle-label': props.isToggleLabel,
    labeled: !!props.label,
  };
});

const iconWrapperClasses = computed(() => {
  return {
    checked: !!modelValue.value,
    hover: isIconWrapperHover.value,
  };
});

function toggleValue(): void {
  emit('update:modelValue', !modelValue.value);
}

function toggleHover(value: boolean): void {
  isIconWrapperHover.value = value;
}

function clickLabel(): void {
  props.isToggleLabel ? toggleValue() : emit('on-click-label');
}
</script>

<style lang="scss" scoped>
.checkbox {
  position: relative;
  display: flex;

  @media (hover: hover) {
    &:hover {
      color: $color-qiwi-50;
    }
  }

  &.toggle-label {
    @media (hover: hover) {
      &:hover {
        > .icon-wrapper {
          border-color: $color-qiwi-50;
        }
      }
    }
  }

  > .icon-wrapper {
    width: 18px;
    height: 18px;
    background-color: #fff;
    border: 1px solid $color-potato-30;
    border-radius: $border-radius-xxs;
    display: grid;
    place-content: center;
    flex-shrink: 0;
    position: relative;
    transition:
      background-color $transition-half ease-in-out,
      border-color $transition-half ease-in-out;
    margin-top: 2px;

    &.hover {
      border-color: $color-qiwi-50;

      &::before {
        content: '';
        position: absolute;
        left: -6px;
        top: -6px;
        border-radius: 50%;
        width: 28px;
        height: 28px;
        background-color: rgba($color-qiwi-50, 0.1);
        z-index: 0;
      }
    }

    &.checked {
      background-color: $color-qiwi-50;
      border-color: $color-qiwi-50;
    }
  }

  > .icon-wrapper > .icon {
    position: absolute;
    top: 3px;
    left: 3px;
    color: #fff;
    transition: opacity $transition-half ease-in-out;
  }

  > .input {
    position: absolute;
    top: 2px;
    left: -1px;
    width: 20px;
    height: 20px;
    opacity: 0;
    cursor: pointer;
  }

  > .label {
    font-size: 14px;
    line-height: 22px;
    padding-left: 10px;
    cursor: pointer;
    transition: color $transition-half ease-in-out;
  }
}
</style>
